/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
// import BatchInfoCard from './BatchInfoCard';
import '../styles/AgregarProducto.css';

const AgregarProducto = () => {
    const { token, userRole } = useAuth();
    const { po_number, cart_code, sku_code, rack, barcode } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showQuantityWarning, setShowQuantityWarning] = useState(false);
    const [warningData, setWarningData] = useState({ finalQuantity: 0, orderedQuantity: 0 });
    const [accumulatedWeight, setAccumulatedWeight] = useState(0);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [canRequestChange, setCanRequestChange] = useState(false);
    const [orderCancelled, setOrderCancelled] = useState(false);
    // The picker is standing at the shelf and is the authority on the real
    // count, so this is an editable field (string, to keep the input
    // controlled) rather than a fixed value computed from what just failed.
    // Prefilled from the backend's available_quantity when it sends one.
    const [foundQuantityInput, setFoundQuantityInput] = useState('');
    const [requestValidationError, setRequestValidationError] = useState('');
    const [requestSent, setRequestSent] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Imprimir el valor de actual_quantity desde la URL
    console.log('Valor de actual_quantity desde la URL:', barcode);

    // Initialize accumulated weight from location.state or current barcode (only for kg products)
    useEffect(() => {
        const previousWeight = location.state?.accumulatedWeight || 0;
        const currentWeight = barcode && barcode.includes('.') ? Number(barcode) : 0;
        setAccumulatedWeight(previousWeight + currentWeight);
    }, [barcode, location.state?.accumulatedWeight]);

    useEffect(() => {
        const fetchItemData = async () => {
            if (!token) {
                setError('No hay token disponible');
                setLoading(false);
                return;
            }

            try {
                let data;
                if (location.state?.orderData) {
                    data = location.state.orderData;
                } else {
                    const response = await fetch(`${API_BASE}/get-po/?po_number=${po_number}`, {
                        headers: { 'Authorization': `Token ${token}` },
                    });

                    if (!response.ok) {
                        throw new Error(`Error al obtener los datos del PO: ${response.statusText}`);
                    }

                    data = await response.json();
                }

                console.log('Datos del PO:', data);

                if (!Array.isArray(data.items) || data.items.length === 0) {
                    throw new Error(`No se encontraron ítems en el pedido con número de PO: ${po_number}`);
                }

                if (!Array.isArray(data.carts) || data.carts.length === 0) {
                    throw new Error('No se encontraron carts para el PO.');
                }

                let foundCart = data.carts.find(cart => cart.code === cart_code);
                if (!foundCart) {
                    throw new Error(`No se encontró el abarrote con código: ${cart_code}`);
                }

                console.log('Cart code:', foundCart.code);

                const foundItem = data.items.find(item => item.sku_code === sku_code);
                if (foundItem) {
                    setItem(prevItem => ({ ...foundItem, cart_code: foundCart.code }));
                    setLoading(false);
                    console.log('Ítem encontrado:', foundItem);
                } else {
                    throw new Error(`No se encontró el ítem con el SKU: ${sku_code}`);
                }
            } catch (error) {
                console.error('Error:', error);
                setError(error.message);
                setLoading(false);
            }
        };

        fetchItemData();
    }, [token, po_number, sku_code, userRole, cart_code, location.state]);

    // Handle "Confirmar" button click - shows popup for kg products
    const handleConfirmClick = () => {
        if (!item) return;

        // For kg products, show confirmation popup
        if (item.format_unit_kg) {
            // Check 1.9x warning before showing popup
            if (accumulatedWeight >= (Number(item.ordered_quantity) * 1.9)) {
                setWarningData({
                    finalQuantity: accumulatedWeight,
                    orderedQuantity: Number(item.ordered_quantity)
                });
                setShowQuantityWarning(true);
                return;
            }
            setShowConfirmPopup(true);
        } else {
            // For non-kg products, use original behavior
            handleConfirm();
        }
    };

    // Final confirmation - sends POST request
    const handleConfirm = async () => {
        if (!item) {
            return;
        }

        const { ordered_quantity, picking_positions } = item;
        const rackValue = picking_positions.rack;
        const shelf = picking_positions.shelf;

        // For kg products, use accumulated weight; for others, use original logic
        const finalQuantity = item.format_unit_kg
            ? accumulatedWeight
            : (barcode && barcode.includes('.')
                ? Number(barcode)
                : Math.floor(Number(ordered_quantity)));

        const postData = {
            po_number,
            cart_code: item.cart_code,
            sku: item.sku,
            actual_quantity: finalQuantity,
            rack: rackValue,
            shelf,
        };

        console.log('Datos que se envían en el POST:', postData);

        try {
            const response = await fetch(`${API_BASE}/update-quantity/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
                body: JSON.stringify(postData),
            });

            if (!response.ok) {
                // Short stock is the common failure here and it is recoverable:
                // offer to ask an admin to lower the line rather than dead-end.
                // The backend reports how much is actually on the shelf
                // (available_quantity, already converted to the line's own
                // unit) — prefill that, but let the picker correct it, since
                // they are the one standing at the shelf.
                // For kg lines, the backend's shortage check compares shelf
                // stock against the *scanned* weight, not the ordered
                // quantity, so available_quantity can come back higher than
                // ordered_quantity. create_quantity_request rejects any
                // requested_quantity above ordered_quantity, so clamp the
                // prefill here — asking for the full ordered amount is the
                // sensible default when there's more on the shelf than the
                // order needs. This also covers the fallback below: when the
                // body can't be parsed or omits the key, availableQuantity
                // stays '' (blank), which trivially never exceeds the cap.
                const orderedQuantity = Number(item.ordered_quantity);
                let availableQuantity = '';
                let hasAvailable = false;
                let errorBody = null;
                try {
                    errorBody = await response.json();
                    if (errorBody && errorBody.available_quantity !== undefined && errorBody.available_quantity !== null) {
                        const parsed = Number(errorBody.available_quantity);
                        if (!Number.isNaN(parsed)) {
                            hasAvailable = true;
                            const clamped = Number.isNaN(orderedQuantity) ? parsed : Math.min(parsed, orderedQuantity);
                            availableQuantity = item.format_unit_kg ? clamped.toFixed(3) : String(clamped);
                        }
                    }
                } catch (parseErr) {
                    // Body wasn't JSON, or had no available_quantity — leave the
                    // field blank rather than guess; the picker fills it in.
                }
                // Not a shortage: sales cancelled the order (or the server
                // refused for another reason). Show its message and don't
                // offer a quantity change request, which would be meaningless.
                if (errorBody && errorBody.cancelled) {
                    setOrderCancelled(true);
                    setError(errorBody.error || 'Pedido anulado.');
                    return;
                }
                if (!hasAvailable && errorBody && errorBody.error) {
                    setError(errorBody.error);
                    return;
                }
                setFoundQuantityInput(availableQuantity);
                setRequestValidationError('');
                setCanRequestChange(true);
                setError('No se pudo registrar la cantidad. Indica cuánto encontraste realmente y solicita una modificación.');
                return;
            }

            const result = await response.json();
            console.log('Respuesta de la actualización:', result);

            navigate(`/picker/${po_number}/${item.cart_code}`);
        } catch (error) {
            console.error('Error en la petición POST:', error);
            setError(error.message);
        }
    };

    const handleRequestChange = async () => {
        // Guard against sending an empty or non-numeric value — this field is
        // free text so the picker can correct the backend's estimate.
        const trimmed = foundQuantityInput.trim();
        const parsedQuantity = Number(trimmed);
        if (trimmed === '' || Number.isNaN(parsedQuantity) || parsedQuantity < 0) {
            setRequestValidationError('Ingresa una cantidad válida (0 o más).');
            return;
        }
        setRequestValidationError('');

        try {
            // Build a short, readable Spanish note that states what the picker
            // entered — never the raw English backend error string.
            const unitLabel = item?.format_unit_kg ? 'kg' : 'unidades';
            const quantityText = item?.format_unit_kg
                ? parsedQuantity.toFixed(3)
                : parsedQuantity;
            const note = `Stock insuficiente en la posición. El picker indica que hay ${quantityText} ${unitLabel} disponibles.`.slice(0, 300);

            const response = await fetch(`${API_BASE}/quantity-requests/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
                body: JSON.stringify({
                    po_number: po_number,
                    item_id: item.id,
                    requested_quantity: parsedQuantity,
                    note,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                setError(data.error || 'No se pudo enviar la solicitud.');
                return;
            }
            setRequestSent(true);
        } catch (err) {
            setError('No se pudo enviar la solicitud. Revisa tu conexión.');
        }
    };

    // Navigate to barcode scanner to scan another product (keeps accumulated weight)
    const handleScanAnother = () => {
        navigate(`/picker/${po_number}/${cart_code}/${sku_code}/${rack}`, {
            state: {
                accumulatedWeight: accumulatedWeight,
                orderData: location.state?.orderData
            }
        });
    };

    // Cancel and reset - goes back to cart
    const handleCancel = () => {
        if (item) {
            console.log('Navegando a:', `/picker/${po_number}/${item.cart_code}`);
            navigate(`/picker/${po_number}/${item.cart_code}`);
        }
    };

    // Rescan from warning - resets accumulated weight
    const handleRescan = () => {
        navigate(`/picker/${po_number}/${cart_code}/${sku_code}/${rack}`);
    };

    if (loading) {
        return (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        );
      }

    if (error) {
        return (
            <div className="agregar-error-screen">
                <p className="agregar-error-message">Error: {error}</p>
                {canRequestChange && !requestSent && (
                    <div className="agregar-request-form">
                        <label htmlFor="found-quantity-input" className="agregar-request-label">
                            {item?.format_unit_kg
                                ? '¿Cuántos kg hay realmente?'
                                : '¿Cuántas unidades hay realmente?'}
                        </label>
                        <input
                            id="found-quantity-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            max={item?.ordered_quantity}
                            step={item?.format_unit_kg ? '0.001' : '1'}
                            value={foundQuantityInput}
                            onChange={(e) => setFoundQuantityInput(e.target.value)}
                            className="agregar-request-input"
                        />
                        {requestValidationError && (
                            <p className="agregar-request-validation-error">{requestValidationError}</p>
                        )}
                        <button
                            type="button"
                            className="agregar-request-button"
                            onClick={handleRequestChange}
                        >
                            Solicitar modificación de cantidad
                        </button>
                    </div>
                )}
                {requestSent && (
                    <p className="agregar-request-sent">
                        Solicitud enviada. Un administrador debe aprobarla; mientras tanto
                        puedes seguir con los demás productos.
                    </p>
                )}
                {orderCancelled ? (
                    <button type="button" className="agregar-back-button" onClick={() => navigate('/picking')}>Volver a Pedidos</button>
                ) : (
                    <button type="button" className="agregar-back-button" onClick={handleCancel}>Volver al carro</button>
                )}
            </div>
        );
    }

    // Warning screen for 1.9x quantity
    if (showQuantityWarning) {
        return (
            <div className="agregar-producto-container">
                <div className="warning-box">
                    <h3>Advertencia</h3>
                    <p>
                        La cantidad escaneada ({warningData.finalQuantity.toFixed(3)} kg) es 1.9 veces o más que la cantidad ordenada ({warningData.orderedQuantity} kg).
                    </p>
                    <p>Esto puede indicar un error de escaneo o duplicación.</p>
                    <button className="warning-button" onClick={handleRescan}>Volver a escanear</button>
                </div>
            </div>
        );
    }

    // Confirmation popup for kg products
    if (showConfirmPopup) {
        return (
            <div className="agregar-producto-container">
                <div className="confirmation-box">
                    <h3>Confirmar cantidad final</h3>
                    <p>
                        <strong>Producto:</strong> {item.sku_name}
                    </p>
                    <p>
                        <strong>Cantidad escaneada:</strong> {accumulatedWeight.toFixed(3)} kg
                    </p>
                    <p>
                        <strong>Cantidad ordenada:</strong> {item.ordered_quantity} kg
                    </p>
                    <button className="confirm-button" onClick={handleConfirm}>Confirmar</button>
                    <button className="cancel-button" onClick={() => setShowConfirmPopup(false)}>Volver</button>
                </div>
            </div>
        );
    }

    // Main view - different for kg products vs regular products
    if (item?.format_unit_kg) {
        // KG product view with accumulated weight display
        return (
            <div className="agregar-producto-container">
                <div className="confirmation-box">
                    <h3>{item.sku_name}</h3>
                    <p className="weight-display">
                        <strong>Escaneado:</strong> {accumulatedWeight.toFixed(3)} kg / <strong>Pedido:</strong> {item.ordered_quantity} kg
                    </p>
                    <button className="confirm-button" onClick={handleScanAnother}>Escanear otro</button>
                    <button className="confirm-button" onClick={handleConfirmClick}>Confirmar</button>
                    <button className="cancel-button" onClick={handleCancel}>Cancelar</button>
                </div>
            </div>
        );
    }

    // Original view for non-kg products
    const displayQuantity = barcode && barcode.includes('.')
        ? `${barcode} kg`
        : item.ordered_quantity;

    return (
        <div className="agregar-producto-container">
            {item ? (
                <div className="confirmation-box">
                    {/* <BatchInfoCard pickingPosition={item.picking_positions} /> */}
                    <p>
                    ¿Agregaste {displayQuantity} {item.format_name}
                    {item.format_name !== 'Unidad' && item.format_unit_kg
                        ? ` de ${item.format_quantity} kg`
                        : item.format_name !== 'Unidad' && !item.format_unit_kg
                        ? ` de ${item.format_quantity} unidades`
                        : ''}
                    {" "}
                    de "{item.sku_name}" al casillero?
                    </p>

                    <button className="confirm-button" onClick={handleConfirm}>Sí</button>
                    <button className="cancel-button" onClick={handleCancel}>Cancelar</button>
                </div>
            ) : (
                <p>No se encontró el producto.</p>
            )}
        </div>
    );
};

export default AgregarProducto;
